import React, { useRef, useState } from 'react'
import SearchInput, { SearchInputPropsApi } from './SearchInput'
import CloseIcon from 'svg/close.svg'
import styles from './HeaderSearch.module.scss'
import classNames from 'classnames'
import { submitSearch } from '../helpers'
import { useHeaderContext } from '../HeaderContext'
import SearchMenuContent from './SearchMenuContent'
import useSearchAutocomplete from './useSearchAutocomplete'
import { useSelector } from 'react-redux'
import { getSearchQuery } from 'selectors/searchProvider'
import Debounce from 'lib/debounce'
import { SearchApi } from 'components/SA/SearchProvider/types'
import { ConstructorSearchService } from 'components/SA/SearchProvider/services/constructor/constructorService'
import useRecentSearches from './useRecentSearches'
import { addStoreLocaleByCookie } from 'lib/storeLocale/client'
import { getUserID } from 'selectors/user/session'
import usePopularSearches from './usePopularSearches'

const debounce = Debounce()

const searchService: SearchApi = new ConstructorSearchService()

const SearchTopHeader = () => {
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<SearchInputPropsApi>(null)
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const searchQuery = useSelector(getSearchQuery)
  const userId = useSelector(getUserID)
  const stringUserId = userId !== 0 ? userId.toString() : ''

  const { mobileHeaderConfig, setMobileHeaderConfig } = useHeaderContext()
  const {
    matchedTerm,
    suggestions,
    getAutocompleteSuggestions,
    setSuggestions,
  } = useSearchAutocomplete({
    searchService,
    maxSuggestions: 5,
  })
  const { recentSearches, addRecentSearch, removeRecentSearch } = useRecentSearches({
    userId: stringUserId,
  })
  const { popularSearches } = usePopularSearches({
    searchService,
    maxPopularSearches: 5,
  })

  const handleRemoveRecentSearchByUserId = (label: string) => {
    removeRecentSearch(label, stringUserId)
  }

  const handleOnSearchQueryChange = (query) => {
    debounce(() => getAutocompleteSuggestions(query || searchQuery), 500)
  }

  const handleFocusSearchInput = (isFocusFlag) => {
    setIsFocused(isFocusFlag)
  }

  const handleFocusOnContentClick = (isFocusFlag: boolean) => {
    handleFocusSearchInput(isFocusFlag)
    searchInputRef?.current?.setIsFocused(isFocusFlag)
  }

  const handleClose = () => {
    setMobileHeaderConfig((previousMobileHeaderConfig) => ({
      ...previousMobileHeaderConfig,
      mobileTopHeader: 'mainTopHeader',
      mobileMenuContent: 'null',
      isMobileMenuOpen: false,
    }))
  }

  const handleSearchSubmit = (query: string) => {
    submitSearch(query)
    if (query.length > 0) {
      addRecentSearch(
        {
          label: query,
          link: addStoreLocaleByCookie(`/all?query=${encodeURIComponent(query)}`),
        },
        stringUserId
      )
    }
    setSuggestions([])
    handleFocusOnContentClick(false)
    searchInputRef?.current?.triggerInputBlur()
  }

  const handleCustomClearSearchCallback = () => {
    setSuggestions([])
  }

  return (
    <div className={styles.searchHeaderWrapper}>
      <div
        className={classNames(styles.searchHeaderContainer, {
          [styles.searchHeaderContainerSearch]: mobileHeaderConfig.mobileTopHeader === 'search',
        })}
      >
        <SearchInput
          ref={searchInputRef}
          onFocusCallback={handleFocusSearchInput}
          onSearch={handleSearchSubmit}
          onChangeCallback={handleOnSearchQueryChange}
          dropdownRef={dropdownRef}
          shouldAutoFocus={true}
          onCustomClearSearchCallback={handleCustomClearSearchCallback}
        />
        <div className={styles.searchHeaderCloseIconBox} onClick={handleClose}>
          <CloseIcon />
        </div>
      </div>
      <div
        ref={dropdownRef}
        className={classNames(styles.searchDropdownBoxMobile, {
          [styles.searchDropdownBoxHidden]: !isFocused,
        })}
      >
        <SearchMenuContent
          suggestions={suggestions}
          recentSearches={recentSearches}
          popularSearches={popularSearches}
          setIsFocused={handleFocusSearchInput}
          onRecentSearchRemove={handleRemoveRecentSearchByUserId}
          onSearch={handleSearchSubmit}
          matchedTerm={matchedTerm}
        />
      </div>
    </div>
  )
}

export default SearchTopHeader
